import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../apiConfig';
import './Settings.css';

// Grouped model options — rendered as <optgroup> in each select
const MODEL_GROUPS = [
  {
    label: 'Anthropic (Claude)',
    options: [
      { value: 'anthropic/claude-haiku-4-5',   label: 'Claude Haiku 4.5  — fast & cheap' },
      { value: 'anthropic/claude-sonnet-4-6',  label: 'Claude Sonnet 4.6 — balanced' },
      { value: 'anthropic/claude-opus-4-6',    label: 'Claude Opus 4.6   — most capable' },
    ],
  },
  {
    label: 'Google (Gemini)',
    options: [
      { value: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite — fastest / cheapest' },
      { value: 'google/gemini-2.5-flash',      label: 'Gemini 2.5 Flash      — fast & balanced' },
      { value: 'google/gemini-2.5-pro',        label: 'Gemini 2.5 Pro        — high capability' },
    ],
  },
  {
    label: 'OpenAI GPT',
    options: [
      { value: 'openai/gpt-4.1-mini', label: 'GPT-4.1 Mini — fast & affordable' },
      { value: 'openai/gpt-4.1',      label: 'GPT-4.1      — balanced' },
      { value: 'openai/gpt-5-mini',   label: 'GPT-5 Mini   — capable' },
      { value: 'openai/gpt-5',        label: 'GPT-5        — frontier' },
    ],
  },
  {
    label: 'OpenAI OSS on Groq (open-weight, fast inference)',
    options: [
      { value: 'groq/openai/gpt-oss-20b',  label: 'GPT-OSS 20B  — very fast, ultra-cheap' },
      { value: 'groq/openai/gpt-oss-120b', label: 'GPT-OSS 120B — fast, low cost' },
    ],
  },
  {
    label: 'Moonshot AI (Kimi K2)',
    options: [
      { value: 'groq/moonshotai/kimi-k2-instruct', label: 'Kimi K2 on Groq     — fast inference' },
      { value: 'moonshot/kimi-k2-turbo-preview',   label: 'Kimi K2 Turbo        — balanced' },
      { value: 'moonshot/kimi-k2.5',               label: 'Kimi K2.5            — strong reasoning' },
      { value: 'moonshot/kimi-k2-thinking',        label: 'Kimi K2 Thinking     — deep reasoning' },
    ],
  },
  {
    label: 'DeepSeek (open-weight)',
    options: [
      { value: 'deepinfra/deepseek-ai/DeepSeek-V3',   label: 'DeepSeek V3   — cheap & capable' },
      { value: 'deepinfra/deepseek-ai/DeepSeek-V3.1', label: 'DeepSeek V3.1 — latest V3' },
      { value: 'deepseek/deepseek-chat',              label: 'DeepSeek Chat — direct API' },
      { value: 'deepseek/deepseek-reasoner',          label: 'DeepSeek R1   — reasoning model' },
    ],
  },
  {
    label: 'GLM / Zhipu AI (open-weight)',
    options: [
      { value: 'deepinfra/zai-org/GLM-4.5-Air', label: 'GLM-4.5 Air — lightest, very cheap' },
      { value: 'deepinfra/zai-org/GLM-4.5',     label: 'GLM-4.5     — balanced open-weight' },
      { value: 'nebius/zai-org/GLM-4.7',        label: 'GLM-4.7     — latest GLM' },
    ],
  },
  {
    label: 'Qwen / Alibaba (open-weight)',
    options: [
      { value: 'deepinfra/Qwen/Qwen3-32B',      label: 'Qwen3 32B       — efficient MoE' },
      { value: 'deepinfra/Qwen/Qwen3-235B-A22B', label: 'Qwen3 235B-A22B — large MoE' },
    ],
  },
  {
    label: 'Meta Llama (open-weight)',
    options: [
      { value: 'deepinfra/meta-llama/Llama-3.3-70B-Instruct', label: 'Llama 3.3 70B — proven open-weight' },
    ],
  },
  {
    label: 'Mistral (open-weight)',
    options: [
      { value: 'mistral/mistral-small-latest', label: 'Mistral Small — fast & cheap' },
      { value: 'mistral/mistral-large-latest', label: 'Mistral Large — balanced' },
    ],
  },
  {
    label: 'xAI (Grok)',
    options: [
      { value: 'xai/grok-3-mini', label: 'Grok 3 Mini — fast reasoning' },
      { value: 'xai/grok-3',      label: 'Grok 3      — full capability' },
    ],
  },
];

const STEPS = [
  { key: 'categorize',          label: 'Step 1 — Categorize & summarize' },
  { key: 'requiresResponse',    label: 'Step 2 — Decide if reply needed' },
  { key: 'draftResponse',       label: 'Step 3A — Draft concise response' },
  { key: 'evaluateSufficiency', label: 'Step 3B — Evaluate draft sufficiency' },
  { key: 'extractInsights',     label: 'Step 3C — Extract insights (no-reply emails)' },
  { key: 'feedbackProcessing',  label: 'Step 4 — Feedback & learning processing' },
];

const STEP_LABELS = {
  categorize:          'Step 1 — Categorize & summarize',
  requiresResponse:    'Step 2 — Decide if reply needed',
  draftResponse:       'Step 3A — Draft concise response',
  evaluateSufficiency: 'Step 3B — Evaluate draft sufficiency',
  extractInsights:     'Step 3C — Extract insights',
  feedbackProcessing:  'Step 4 — Feedback & learning',
};

const formatTokens = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const formatCost = (n) => {
  if (n === 0) return '$0.00';
  if (n < 0.0001) return '<$0.0001';
  if (n < 0.01)   return `$${n.toFixed(4)}`;
  return `$${n.toFixed(4)}`;
};

// Performance badge component for a single step
const PerfBadge = ({ accuracy, total }) => {
  if (accuracy === null || accuracy === undefined || total === 0) {
    return <span className="perf-badge perf-badge--gray">—</span>;
  }
  const cls = accuracy >= 80 ? 'perf-badge--green' : accuracy >= 60 ? 'perf-badge--yellow' : 'perf-badge--red';
  const icon = accuracy >= 80 ? '✓' : accuracy >= 60 ? '~' : '✗';
  return <span className={`perf-badge ${cls}`}>{accuracy}% {icon}</span>;
};

const Settings = () => {
  const [models, setModels] = useState({});
  const [modelOptimization, setModelOptimization] = useState({
    enabled: true, minDaysBetweenChanges: 7, minSamplesForChange: 5, accuracyThresholdPct: 70,
  });
  const [modelChangeHistory, setModelChangeHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', message }

  const [usage, setUsage] = useState(null);        // null = not yet loaded
  const [usageLoading, setUsageLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [perfByStep, setPerfByStep] = useState({}); // step → { accuracy, total } for current model

  useEffect(() => {
    fetch(`${API_BASE_URL}/settings`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setModels(data.models ?? {});
        setModelOptimization(prev => ({ ...prev, ...(data.modelOptimization ?? {}) }));
        setModelChangeHistory(data.modelChangeHistory ?? {});
        setLoading(false);
      })
      .catch(() => {
        setStatus({ type: 'error', message: 'Failed to load settings.' });
        setLoading(false);
      });

    fetch(`${API_BASE_URL}/usage`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setUsage(data);
        setUsageLoading(false);
      })
      .catch(() => {
        setUsageLoading(false);
      });
  }, []);

  // Recompute perfByStep whenever models or usage.performance changes
  useEffect(() => {
    const perf = usage?.performance ?? [];
    if (perf.length === 0 || Object.keys(models).length === 0) { setPerfByStep({}); return; }
    const map = {};
    for (const entry of perf) {
      if (models[entry.step] === entry.model) {
        map[entry.step] = { accuracy: entry.accuracy, total: entry.total };
      }
    }
    setPerfByStep(map);
  }, [models, usage]);

  const handleResetUsage = () => {
    setResetting(true);
    fetch(`${API_BASE_URL}/usage`, { method: 'DELETE', credentials: 'include' })
      .then(() => {
        setUsage({ totalCalls: 0, totalCost: 0, byModel: {}, byStep: {}, performance: [] });
      })
      .catch(() => {})
      .finally(() => setResetting(false));
  };

  const handleChange = (step, value) => {
    setModels(prev => ({ ...prev, [step]: value }));
  };

  const handleOptimizationChange = (field, value) => {
    setModelOptimization(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setSaving(true);
    setStatus(null);
    fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ models, modelOptimization }),
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
        return data;
      })
      .then(() => {
        setStatus({ type: 'success', message: 'Settings saved successfully.' });
        setTimeout(() => setStatus(null), 3000);
      })
      .catch(err => {
        setStatus({ type: 'error', message: `Save failed: ${err.message}` });
        setTimeout(() => setStatus(null), 4000);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="settings-page">
      <div className="settings-page__head">
        <h1 className="settings-page__title">Settings</h1>
        <p className="settings-page__subtitle">
          Configure which AI model runs each step of the email processing pipeline.
          Open-weight models (GLM, DeepSeek, Kimi, Qwen, Llama) are significantly cheaper
          for simple classification and extraction tasks.
        </p>
      </div>

      {loading ? (
        <div className="settings-loading">
          <div className="spinner" />
          <span>Loading settings…</span>
        </div>
      ) : (
        <>
          <div className="settings-section">
            <h2 className="settings-section__title">AI Pipeline Models</h2>
            <p className="settings-section__desc">
              Steps 1, 2, and 3C are classification/extraction tasks — cheap fast models work well.
              Step 3A (drafting) benefits from a stronger model. Step 3B is a short quality check.
            </p>
            <div className="settings-steps">
              {STEPS.map(({ key, label }) => {
                const perf = perfByStep[key];
                const isTrackable = key === 'categorize' || key === 'requiresResponse';
                return (
                  <div key={key} className="settings-step-row">
                    <label className="settings-step-row__label" htmlFor={`model-${key}`}>
                      {label}
                    </label>
                    <select
                      id={`model-${key}`}
                      className="settings-select"
                      value={models[key] ?? ''}
                      onChange={e => handleChange(key, e.target.value)}
                    >
                      {MODEL_GROUPS.map(group => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {isTrackable && !usageLoading && (
                      <PerfBadge accuracy={perf?.accuracy ?? null} total={perf?.total ?? 0} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Auto-Optimization ── */}
          <div className="settings-section">
            <h2 className="settings-section__title">Auto-Optimization</h2>
            <p className="settings-section__desc">
              When feedback is processed, the AI can automatically switch underperforming models
              (Steps 1 &amp; 2 only — the correctable steps) to better alternatives. A cooldown
              prevents thrashing between models.
            </p>
            <div className="settings-toggle-row">
              <input
                type="checkbox"
                id="opt-enabled"
                checked={modelOptimization.enabled ?? true}
                onChange={e => handleOptimizationChange('enabled', e.target.checked)}
              />
              <label htmlFor="opt-enabled">Enable automatic model switching</label>
            </div>
            <div className="settings-number-grid">
              <div className="settings-number-row">
                <div className="settings-number-row__label">
                  Min days between changes
                  <div className="settings-number-row__desc">Cooldown before the same step can be switched again (1–90)</div>
                </div>
                <input
                  type="number"
                  className="settings-number-input"
                  min={1} max={90}
                  value={modelOptimization.minDaysBetweenChanges ?? 7}
                  onChange={e => handleOptimizationChange('minDaysBetweenChanges', Number(e.target.value))}
                />
              </div>
              <div className="settings-number-row">
                <div className="settings-number-row__label">
                  Min samples before switching
                  <div className="settings-number-row__desc">Number of emails a model must have processed before it can be replaced (1–50)</div>
                </div>
                <input
                  type="number"
                  className="settings-number-input"
                  min={1} max={50}
                  value={modelOptimization.minSamplesForChange ?? 5}
                  onChange={e => handleOptimizationChange('minSamplesForChange', Number(e.target.value))}
                />
              </div>
              <div className="settings-number-row">
                <div className="settings-number-row__label">
                  Accuracy threshold %
                  <div className="settings-number-row__desc">If a model's accuracy falls below this, it's eligible for replacement (50–95)</div>
                </div>
                <input
                  type="number"
                  className="settings-number-input"
                  min={50} max={95}
                  value={modelOptimization.accuracyThresholdPct ?? 70}
                  onChange={e => handleOptimizationChange('accuracyThresholdPct', Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          {status && (
            <div className={`settings-status settings-status--${status.type}`}>
              {status.message}
            </div>
          )}

          <div className="settings-actions">
            <button
              className="btn btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>

          {/* ── LLM Cost Tracking ── */}
          <div className="settings-section">
            <div className="cost-header">
              <h2 className="settings-section__title">LLM Cost Tracking</h2>
              {usage && usage.totalCalls > 0 && (
                <button
                  className="cost-reset-btn"
                  onClick={handleResetUsage}
                  disabled={resetting}
                >
                  {resetting ? 'Clearing…' : 'Clear stats'}
                </button>
              )}
            </div>
            <p className="settings-section__desc">
              Estimated cost based on recorded token usage. Prices are approximate and may not reflect
              your actual billing (routing via Requestly may have different rates).
            </p>

            {usageLoading ? (
              <div className="cost-loading">
                <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                <span>Loading usage data…</span>
              </div>
            ) : !usage || usage.totalCalls === 0 ? (
              <p className="cost-empty">No usage recorded yet. Run a sync to start tracking costs.</p>
            ) : (
              <>
                {/* Summary row */}
                <div className="cost-summary">
                  <div className="cost-summary__item">
                    <span className="cost-summary__value">{formatCost(usage.totalCost)}</span>
                    <span className="cost-summary__label">Total estimated cost</span>
                  </div>
                  <div className="cost-summary__item">
                    <span className="cost-summary__value">{usage.totalCalls.toLocaleString()}</span>
                    <span className="cost-summary__label">API calls</span>
                  </div>
                </div>

                {/* By model */}
                {Object.keys(usage.byModel).length > 0 && (
                  <div className="cost-subsection">
                    <div className="cost-subsection__title">By Model</div>
                    <table className="cost-table">
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>Calls</th>
                          <th>Tokens in</th>
                          <th>Tokens out</th>
                          <th>Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(usage.byModel)
                          .sort((a, b) => b[1].estimatedCost - a[1].estimatedCost)
                          .map(([model, stats]) => (
                            <tr key={model}>
                              <td><span className="cost-model-name">{model}</span></td>
                              <td>{stats.calls}</td>
                              <td>{formatTokens(stats.promptTokens)}</td>
                              <td>{formatTokens(stats.completionTokens)}</td>
                              <td>
                                {stats.hasPricing
                                  ? formatCost(stats.estimatedCost)
                                  : <span className="cost-no-pricing">—</span>
                                }
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* By pipeline step */}
                {Object.keys(usage.byStep).length > 0 && (
                  <div className="cost-subsection">
                    <div className="cost-subsection__title">By Pipeline Step</div>
                    <table className="cost-table">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Calls</th>
                          <th>Tokens in</th>
                          <th>Tokens out</th>
                          <th>Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(usage.byStep)
                          .sort((a, b) => b[1].estimatedCost - a[1].estimatedCost)
                          .map(([step, stats]) => (
                            <tr key={step}>
                              <td>{STEP_LABELS[step] ?? step}</td>
                              <td>{stats.calls}</td>
                              <td>{formatTokens(stats.promptTokens)}</td>
                              <td>{formatTokens(stats.completionTokens)}</td>
                              <td>{formatCost(stats.estimatedCost)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <p className="cost-disclaimer">
                  * Pricing data is approximate. Models without known pricing show —.
                  Token counts depend on what Requestly returns in the API response.
                </p>

                {/* Model change history */}
                <div className="cost-subsection">
                  <div className="cost-subsection__title">Auto-Optimization History</div>
                  {Object.keys(modelChangeHistory).length === 0 ? (
                    <p className="model-change-history-empty">No automatic model changes recorded yet.</p>
                  ) : (
                    <table className="cost-table">
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(modelChangeHistory).map(([step, change]) => (
                          <tr key={step}>
                            <td>{STEP_LABELS[step] ?? step}</td>
                            <td><span className="cost-model-name">{change.from}</span></td>
                            <td><span className="cost-model-name">{change.to}</span></td>
                            <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                              {change.at ? new Date(change.at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Settings;
